import { usePocket } from "@/contexts/PocketContext";
import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import type { Agent } from "@/hooks/use-agents";

export interface Sale {
  id: string;
  agent: string;
  product?: string;
  productType: string;
  amount: number;
  commission: number;
  commissionRate: number;
  status: "unpaid" | "paid";
  saleDate: string;
  created: string;
  updated: string;
  expand?: {
    agent?: {
      id: string;
      code: string;
      name: string;
      company?: string;
    };
    product?: {
      id: string;
      name: string;
    };
  };
}

export function useSales(filters?: {
  agent?: string;
  status?: "unpaid" | "paid" | "all";
  dateFrom?: string;
  dateTo?: string;
}) {
  const { pb, user } = usePocket();
  const { mutate: globalMutate } = useSWRConfig();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const filterKey = filters
    ? `sales-${JSON.stringify(filters)}-${user?.id}`
    : `sales-${user?.id}`;

  const { data, mutate, error: swrError } = useSWR<Sale[]>(
    user ? filterKey : null,
    async () => {
      if (!user) return [];
      let filter: string | undefined = undefined;
      if (!user.isSuperAdmin) {
        const userAgents = await pb.collection("agents").getFullList<Agent>({
          filter: `createdBy = "${user.id}"`,
          fields: "id",
        });
        if (userAgents.length === 0) {
          return [];
        }
        const agentIds = userAgents.map(a => `agent = "${a.id}"`).join(" || ");
        filter = `(${agentIds})`;
      }
      if (filters?.agent) {
        if (filter) filter += " && ";
        else filter = "";
        filter += `agent = "${filters.agent}"`;
      }
      if (filters?.status && filters.status !== "all") {
        if (filter) filter += " && ";
        else filter = "";
        filter += `status = "${filters.status}"`;
      }
      if (filters?.dateFrom) {
        if (filter) filter += " && ";
        else filter = "";
        filter += `saleDate >= "${filters.dateFrom}"`;
      }
      if (filters?.dateTo) {
        if (filter) filter += " && ";
        else filter = "";
        filter += `saleDate <= "${filters.dateTo}"`;
      }
      const records = await pb.collection("sales").getFullList<Sale>({
        ...(filter ? { filter } : {}),
        expand: "agent,product",
        sort: "-saleDate",
      });
      return records;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    }
  );

  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setIsLoading(false);
    } else if (data !== undefined) {
      setIsLoading(false);
      setError(null);
    }
  }, [data, swrError]);

  const createSale = async (data: {
    agent: string;
    product?: string;
    productType: string;
    amount: number;
    commission: number;
    commissionRate: number;
    saleDate: string;
  }) => {
    const record = await pb.collection("sales").create<Sale>({
      ...data,
      status: "unpaid",
    });
    await mutate();
    setTimeout(() => {
      globalMutate((key) => typeof key === "string" && key.startsWith("sales-"));
    }, 100);
    return record;
  };

  const updateSale = async (id: string, data: Partial<Sale>) => {
    const record = await pb.collection("sales").update<Sale>(id, data);
    await mutate();
    await globalMutate((key) => typeof key === "string" && key.startsWith("sales-"));
    return record;
  };

  const deleteSale = async (id: string) => {
    try {
      // Проверяем, есть ли связанные выплаты (если есть коллекция payouts)
      // Пока просто удаляем, так как выплаты могут быть отдельной логикой
      await pb.collection("sales").delete(id);
      await mutate();
      await globalMutate((key) => typeof key === "string" && key.startsWith("sales-"));
    } catch (err: unknown) {
      const error = err as { message?: string; response?: { data?: { message?: string } } };
      if (error.message) {
        throw new Error(error.message);
      }
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error("Ошибка при удалении продажи. Возможно, у неё есть связанные записи.");
    }
  };

  return {
    sales: data || [],
    isLoading,
    error,
    createSale,
    updateSale,
    deleteSale,
    refresh: mutate,
  };
}

export function useSalesByAgent(agentId: string | null, requireAuth = false) {
  const { pb, user } = usePocket();
  const { mutate: globalMutate } = useSWRConfig();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const filterKey = `sales-agent-${agentId}`;

  const { data, mutate, error: swrError } = useSWR<Sale[]>(
    agentId ? filterKey : null,
    async () => {
      if (!agentId) return [];
      if (requireAuth && !user) return [];
      let filter = `agent = "${agentId}" && status = "unpaid"`;
      try {
        const records = await pb.collection("sales").getFullList<Sale>({
          filter,
          fields: "id,agent,product,productType,amount,commission,commissionRate,status,saleDate,created,updated",
          sort: "-saleDate",
        });
        return records;
      } catch (err: any) {
        if (err.status === 401 || err.status === 403) {
          const records = await pb.collection("sales").getFullList<Sale>({
            filter,
            fields: "id,agent,product,productType,amount,commission,commissionRate,status,saleDate,created,updated",
            sort: "-saleDate",
          });
          return records;
        }
        throw err;
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
    }
  );

  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setIsLoading(false);
    } else if (data !== undefined) {
      setIsLoading(false);
      setError(null);
    }
  }, [data, swrError]);

  const createSale = async (data: {
    agent: string;
    product?: string;
    productType: string;
    amount: number;
    commission: number;
    commissionRate: number;
    saleDate: string;
  }) => {
    try {
      console.log("Creating sale with data:", data);
      const record = await pb.collection("sales").create<Sale>({
        ...data,
        status: "unpaid",
      });
      console.log("Sale created successfully:", record);
      await mutate();
      setTimeout(() => {
        globalMutate((key) => typeof key === "string" && key.startsWith("sales-"));
      }, 100);
      return record;
    } catch (err: any) {
      console.error("Error creating sale:", err);
      if (err.response) {
        console.error("Error response:", err.response);
        console.error("Error data:", err.response.data);
      }
      throw err;
    }
  };

  return {
    sales: data || [],
    isLoading,
    error,
    createSale,
    refresh: mutate,
  };
}

